
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { mapGetters, mapState } from 'vuex';
import { GlobalState } from '@/shared/models/GlobalState';
import { adService } from './AdService';
import FlashMessage from '@/shared/components/FlashMessage.vue';
import FixedElement from '@/shared/components/FixedElement.vue';
import { Message, MessageType } from '@/shared/models/Message';
import { HandleDirective } from 'vue-slicksort';
import AdPreview from './AdPreview.vue';
import { environment } from '@/environment/environment';
import useVuelidate from '@vuelidate/core';
import { scrollToAnchor } from '@/shared/helpers/scrollToAnchor';
import Multiselect from 'vue-multiselect';
import { accountService } from '../account/AccountService';
import AdDeveloperProjectInfo from '@/ad/components/AdDeveloperProjectInfo.vue';
import AdDeveloperProjectItem from '@/ad/components/AdDeveloperProjectItem.vue';
import AdDeveloperProjectList from '@/ad/components/AdDeveloperProjectList.vue';

export default defineComponent({
  mixins: [Form],
  setup() {
    return { v$: useVuelidate({ $scope: false }) };
  },
  components: {
    FlashMessage,
    AdPreview,
    FixedElement,
    Multiselect,
    AdDeveloperProjectInfo,
    AdDeveloperProjectItem,
    AdDeveloperProjectList,
  },
  props: {
    adFrontId: String,
  },
  directives: { handle: HandleDirective },
  data() {
    return {
      bottomThreshold: 289,
      showPreview: false,
      isHeaderFixed: false,
      messages: [] as Message[],
      notificationClass: '',
      isPublished: false,
      isConfirmEmailSent: false,
      publishStatuses: ['publish', 'archive'],
      publishStatus: 'publish',
      form: {} as any,
      showVirtualTourModal: false,
      showVideoModal: false,
    };
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.preventNav);
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    const shouldAlert = this.isEditing;
    if (shouldAlert) {
      const answer = window.confirm(this.$t('page-leave-confirm'));
      if (!answer) {
        next(false);
        return;
      }
    }
    next();
  },
  watch: {
    windowWidth: function () {
      this.updateBottomThreshold();
    },
  },
  async created() {
    this.updateBottomThreshold();
    this.isLoading = true;
    try {
      await this.loadData();
      scrollToAnchor(this.$route.hash);
      this.handleFlashMessages();
    } catch (error) {
      console.error(error);
    }
  },
  computed: {
    ...mapState({
      user: (state) => (state as GlobalState).user,
    }),
    ...mapGetters(['windowWidth', 'isMobile']),
    isEditing(): boolean {
      return this.v$.$anyDirty && this.v$.$invalid;
    },
    showFormMessages(): boolean {
      return this.isSuccess || this.isError || this.errors.length > 0;
    },
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      const result = await adService.getById(this.adFrontId ? this.adFrontId : '');
      this.form = Object.assign({}, this.form, result.data.model);
      if (!result.data.model) {
        this.handleFlashMessages([new Message(this.$t('AdForm.not-found'), MessageType.Error)], true);
        return;
      }
      this.isPublished = this.form.isPublished;
      this.statuses = result.data.statuses;
      this.isLoading = false;
    },
    updateBottomThreshold() {
      const footer = document.querySelector('footer');
      if (footer) {
        this.bottomThreshold = footer.clientHeight;
      }
    },
    preventNav(event: any) {
      if (!this.isEditing || environment !== 'production') {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
      if (localStorage && !this.isEdit) {
        const form = this.beforeSave();
        localStorage.setItem('adDeveloperForm', JSON.stringify(Object.assign({}, form, { password: null, confirmPassword: null })));
      }
    },
    updateFixedStatus(isFixed: boolean) {
      this.isHeaderFixed = isFixed;
    },
    handleFlashMessages(appendMessages: Message[] = [], notFound = false) {
      const messages = [] as any[];
      if (!notFound) {
        if (!this.form.isPublished) {
          if (!this.form.isEmailConfirmed) {
            messages.push(new Message(this.$t('AdDeveloperProjectForm.saved-not-published-not-confirmed'), MessageType.Warning, 'confirmemail'));
          } else {
            messages.push(new Message(this.$t('AdDeveloperProjectForm.saved-not-published'), MessageType.Error));
          }
        }
      }
      if (appendMessages && appendMessages.length > 0) {
        messages.push(...appendMessages);
      }
      this.messages = messages;
      this.notificationClass = this.getNotificationClass();
    },
    getNotificationClass() {
      const errorExists = this.messages.some((x) => x.type === MessageType.Error);
      if (errorExists) return 'is-danger';
      const warningExists = this.messages.some((x) => x.type === MessageType.Warning);
      if (warningExists) return 'is-warning';
      return 'is-success';
    },
    async sendConfirmEmail() {
      try {
        this.isLoading = true;
        await accountService.resetConfirmEmail();
        this.isConfirmEmailSent = true;
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    onPublishChanged() {
      this.publish(this.isPublished);
    },
    async onArchivedChanged() {
      try {
        this.isLoading = true;
        if (!this.form.isArchived) {
          await adService.archive(this.form.adId);
        } else {
          await adService.unarchive(this.form.adId);
        }
        await this.loadData();
        this.handleFlashMessages();
      } catch (error) {
        console.error(error);
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async publish(value: boolean) {
      this.isInserted = false;
      try {
        this.isLoading = true;
        if (value) {
          await adService.publish(this.form.adId);
        } else {
          await adService.unpublish(this.form.adId);
        }
        this.form.isPublished = value;
        if (value) {
          this.handleFlashMessages([new Message(this.$t('AdDeveloperProjectForm.saved-published'), MessageType.Success)]);
        } else {
          this.handleFlashMessages();
        }
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    async onShowPreview() {
      this.showPreview = true;
    },
    onVirtualTour() {
      this.showVirtualTourModal = true;
    },
    onVideo() {
      //this.showVideoModal = true;
      window.open(this.form.videoLink);
    },
    onEstateInserted() {
      this.$refs.adDeveloperProjectListComponent.search(true);
    },
  },
});
